import { useMemo } from "react";
import useCustomerData from "./useCustomerData";
import { resolveBusinessPanels, normaliseBusinessProfile } from "../lib/resolveBusinessPanels";

/**
 * Reads the business adaptivity profile (stored inside
 * `customerData.params.business_profile`) and runs it through the resolver
 * to produce the tier-tagged panel list the UI should render.
 *
 * Returns:
 *  - profile        : normalised business_profile object
 *  - panels         : flat list of resolved panel objects (with .tier, .originType, etc.)
 *  - tiers          : count of panels per tier (core / primary / secondary / modifier / strategic)
 *  - hasProfile     : true if a primary_business_model is set
 *  - customerData   : the full dashboard payload (so panel bodies can read real values)
 *  - dataAvailable  : true once the first Tally sync has landed
 *  - isLoading      : true while customerData fetch is in flight
 *  - companyName    : convenience accessor
 */
export default function useBusinessProfile() {
  const { customerData, dataAvailable, isLoading, companyName } = useCustomerData();
  const rawProfile = customerData?.params?.business_profile || null;

  const { profile, panels, tiers, hasProfile } = useMemo(() => {
    const norm = normaliseBusinessProfile(rawProfile);
    const r = resolveBusinessPanels(norm);
    return {
      profile:    r.profile,
      panels:     r.panels,
      tiers:      r.tiers,
      hasProfile: !!norm.primary_business_model,
    };
  }, [JSON.stringify(rawProfile)]);

  return { profile, panels, tiers, hasProfile, customerData, dataAvailable, isLoading, companyName };
}
